import { useDrag } from "client/hooks/utils/useDrag";
import { useKeyboard } from "client/hooks/utils/useKeyboard";
import { useOnOutsideClick } from "client/hooks/utils/useOnOutsideClick";
import { inside } from "client/utils/collisionDetection";
import { useMemeEditor } from "components/modules/Editors/MemeGenerator/MemeEditorContext";
import { useRef } from "react";
import { editIcon, removeIcon, setIconSettings } from "store/features/memeGenerator/actions";
import { MemeGeneratorState, MemeIconState } from "store/features/memeGenerator/types";
import {
    calculateEPosition,
    calculateNEPosition,
    calculateNPosition,
    calculateSEPosition,
    calculateSPosition,
    calculateSWPosition,
    calculateWPosition,
} from "store/features/memeGenerator/utils/useResizeHandlers";
import { store, useAppDispatch, useAppSelector } from "store/store";

function getIconState(id: string): MemeIconState | undefined {
    return store.getState().memeGenerator.meme?.icons.find((i) => i.id === id)?.state;
}

export const useIcon = (icon: NonNullable<MemeGeneratorState["meme"]>["icons"][number]) => {
    const dispatch = useAppDispatch();
    const isDownloading = !!useAppSelector((state) => state.memeGenerator.meme?.isDownloading);
    const iconContainerRef = useRef<HTMLDivElement>(null);

    function getIcon() {
        return store.getState().memeGenerator.meme?.icons.find((_i) => _i.id === icon.id);
    }

    function handleOutsideClick() {
        const icon = getIcon();
        if (
            !icon ||
            !!icon.draggingStage?.rotate ||
            !!icon.draggingStage?.resize ||
            !!icon.draggingStage?.position ||
            icon.state !== MemeIconState.ACTIVE
        )
            return;
        makeInactive();
    }

    function handleDelete() {
        if (getIconState(icon.id) !== MemeIconState.ACTIVE) return;
        dispatch(removeIcon(icon.id));
    }

    useOnOutsideClick(iconContainerRef, icon.state === MemeIconState.ACTIVE, handleOutsideClick, [
        "delete-icon-button",
    ]);
    useKeyboard("Delete", icon.state === MemeIconState.ACTIVE, handleDelete);
    useKeyboard("Backspace", icon.state === MemeIconState.ACTIVE, handleDelete);

    function makeActive() {
        dispatch(
            editIcon({
                id: icon.id,
                state: MemeIconState.ACTIVE,
            }),
        );
    }

    function makeInactive() {
        dispatch(
            editIcon({
                id: icon.id,
                state: MemeIconState.INACTIVE,
                showBorder: false,
            }),
        );
    }

    function iconContainerClick() {
        makeActive();
    }

    function onEnter() {
        dispatch(
            editIcon({
                id: icon.id,
                showBorder: true,
            }),
        );
    }

    function onLeave() {
        getIconState(icon.id) === MemeIconState.INACTIVE &&
            dispatch(
                editIcon({
                    id: icon.id,
                    showBorder: false,
                }),
            );
    }

    function onPointerDown() {
        makeActive();
    }

    function onPointerMove(left: number, top: number) {
        const icon = getIcon();
        if (!icon) return;
        dispatch(
            setIconSettings({
                id: icon.id,
                left: icon.settings.left + left,
                top: icon.settings.top + top,
            }),
        );
    }

    const { itemsContainerRef } = useMemeEditor();

    function onPointerUp() {
        const icon = getIcon();
        const settings = icon?.settings;
        const itemsContainer = itemsContainerRef?.current;
        const memeSettings = store.getState().memeGenerator.meme?.settings;
        if (!icon || !settings || !itemsContainer || !memeSettings) return;
        const { image } = memeSettings;
        const item = {
            left: settings.left,
            top: settings.top,
            width: settings.width,
            height: settings.height,
            rotation: settings.rotation,
        };
        const [nX, nY] = calculateNPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const [wX, wY] = calculateWPosition(item);
        const [seX, seY] = calculateSEPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const [sX, sY] = calculateSPosition(item);
        const [eX, eY] = calculateEPosition(item);
        const textBoxPoints = [
            [image.left + settings.left, -image.top - settings.top],
            [image.left + nX, -image.top - nY],
            [image.left + neX, -image.top - neY],
            [image.left + eX, -image.top - eY],
            [image.left + seX, -image.top - seY],
            [image.left + sX, -image.top - sY],
            [image.left + swX, -image.top - swY],
            [image.left + wX, -image.top - wY],
        ];
        const boundarySlice = 20;
        const containerPoints = [
            [image.left + boundarySlice, -boundarySlice],
            [image.left + itemsContainer.getBoundingClientRect().width - 20, -boundarySlice],
            [
                image.left + itemsContainer.getBoundingClientRect().width - 20,
                -itemsContainer.getBoundingClientRect().height + boundarySlice,
            ],
            [image.left + boundarySlice, -itemsContainer.getBoundingClientRect().height + boundarySlice],
        ];
        const isInside = textBoxPoints.some((point) => inside(point, containerPoints));
        if (!isInside) {
            dispatch(removeIcon(icon.id));
        }
    }

    const options: Parameters<typeof useDrag>[0] = {
        onEnter,
        onLeave,
        onPointerDown,
        onPointerMove,
        onPointerUp,
    };

    const { dragHandlers } = useDrag(options);

    return {
        isDownloading,
        iconContainerRef,
        iconContainerClick,
        dragHandlers,
    };
};
